// import moment from 'moment';
import { AnyAction } from 'redux';
import { handleActions, combineActions } from 'redux-actions';

import { logoutAction } from '@reducers/auth/AuthActionConfig';
import {
  getPreferredDentistReferralAction, getAffiliatedDentistReferralsAction,
  getUnreadAffiliatedReferralsCountAction, getUnreadPreferredDentistReferralsCountAction,
} from '@reducers/clinic/ClinicActionConfig';
import { getMessageListAction, getClinicMessageListAction } from '@reducers/dashboard/DashboardActionConfig';
import {
  updateUnreadLabOrderMessageCountAction,
  markLabOrderConversationInListReadAction,
  markLabOrderConversationInListUnreadAction,
} from '@reducers/lab-order/LabOrderActionConfig';
import { getUnreadNotificationsAction } from '@reducers/shared/SharedActionConfig';

import {
  showReferralAction,
  clearReferralDataAction,
  createPatientInformationAction,
  updatePatientInformationAction,
  createPatientHistoryAction,
  updatePatientHistoryAction,
  saveReferralInfoAction,
  saveReferralReasonAction,
  getReferralMessagesAction,
  updateReferralStatusAction,
  sendReferralMessageAction,
  displayMessageAction,
  updateMessageAction,
  deleteMessageAction,
  updateUnreadReferralMessageCountAction,
  referralMessageArrivedAction,
  getReferralsAction,
  getClinicReferralsAction,
  showClinicReferralAction,
  markReferralConversationReadAction,
  markReferralConversationInListReadAction,
  markReferralConversationInListUnreadAction,
  updateReferralListAction,
  updateReferralMessageStatusAction,
  deleteReferralMessageAttachmentAction,
  deleteReceiverMessageAction,
  deleteReferralAttachmentAction,
  getReferralMessageThreadAction,
  getReferralMessageAttachmentsAction,
  saveReferralLabInfoAction,
  saveReferralLabOrderDetailsAction,
  showLabOrderAction,
  markAsUrgentAction,
  getAffiliatedReferralsAction,
  updateReferralInfoAction,
  updateReferralLabInfoAction,
  updateReferralFollowUpStatusAction,
  generateSummaryFileRequestAction,
  updateReferralFilterAction,
  updateReferralReferredByAction,
  updateReferralReferredToAction,
} from './ReferralActionConfig';

export type FilterType = 'all' | 'sent' | 'received';
type ReferralStatus = 'pending' | 'accepted' | 'scheduled' | 'ongoing' | 'completed' | 'declined' | 'all';
interface UnreadCount {
  sent: Record<ReferralStatus, number>;
  received: Record<ReferralStatus, number>;
  all: Record<ReferralStatus, number>;
}
// reducers
const defaultState = {
  referralData: {} as any,
  conversations: {} as { [key: string]: any[] },
  pagination: {},
  unreadCount: {
    sent: {
      pending: 0,
      accepted: 0,
      scheduled: 0,
      ongoing: 0,
      completed: 0,
      declined: 0,
      all: 0,
    },
    received: {
      pending: 0,
      accepted: 0,
      scheduled: 0,
      ongoing: 0,
      completed: 0,
      declined: 0,
      all: 0,
    },
    all: {
      pending: 0,
      accepted: 0,
      scheduled: 0,
      ongoing: 0,
      completed: 0,
      declined: 0,
      all: 0,
    },
  } as UnreadCount,
  referralList: {} as any,
  messageList: {},
  isUrgent: false,
  filter: 'all' as FilterType,
  referralFilter: [
    { label: 'All', value: 'all' },
  ],
  referralReferredBy: [ [ 'all', 'all' ] ] as [string, string][],
  referralReferredTo: [ [ 'all', 'all' ] ] as [string, string][],
  // ENH-054: how many referral messages arrived live; pages showing counts refetch on it.
  liveMessages: 0,
};

type State = typeof defaultState;

const statusArr = [ 'pending', 'accepted', 'scheduled', 'ongoing', 'completed', 'declined' ];

// BUG-021 cache coherence helper.
//
// Several actions (showReferral, createPatientInformation, updatePatientInformation,
// updateReferralInfo, updateReferralStatus, etc.) used to update `referralData`
// (the single referral being viewed) without also updating the matching entry in
// `referralList.data` (the cached referrals list / search results). That left
// the cached list stale whenever the user edited a referral from inside the
// list page (modal-based edits don't unmount ReferralsPage, so its mount-time
// refetch effect doesn't fire). The symptom was the classic "patient sometimes
// disappears from search, fixed by logout/login" — logout drops the entire
// Redux store and the next login re-fetches everything.
//
// This is the same pattern Apollo Client and RTK Query use under the hood:
// after a successful mutation, sync the normalized cache in place. It runs in
// constant memory, makes no API calls, and is a pure no-op when:
//   - the payload has no usable id (e.g. message-thread payloads)
//   - the list is empty (nothing to sync)
//   - the id isn't present in the cached list (different filter or page —
//     the cache will refresh on the next list-level fetch instead)
//
// Handles both wrapping conventions used in this codebase: some endpoints
// return the referral as `payload` directly, others as `payload.data`.
//
// Critically, the helper returns the EXACT same `referralList` reference when
// no entry actually matches. That single property does most of the work of
// keeping this safe:
//
//   - Components subscribed to `referralList` via react-redux's default
//     shallow equality don't re-render on actions where nothing changed in
//     the list (e.g., showReferralAction for a referral not in the current
//     page / filter).
//
//   - Actions that return a fundamentally different entity but share the id
//     namespace (e.g., showLabOrderAction returning a lab-order Referral row
//     while referralList holds type=referral rows — the rows have distinct
//     UUIDs because they live in the same table) naturally never match, so
//     the helper no-ops on them instead of merging incompatible fields.
const syncReferralInList = (referralList: any, payload: any) => {
  const updated = payload?.data?.id ? payload.data : payload;
  const updatedId = updated?.id;
  if (!updatedId || !Array.isArray(referralList?.data) || referralList.data.length === 0) {
    return referralList;
  }
  const matchIndex = referralList.data.findIndex((ref: any) => ref?.id === updatedId);
  if (matchIndex === -1) {
    return referralList;
  }
  const newData = referralList.data.slice();
  newData[matchIndex] = { ...newData[matchIndex], ...updated };
  return {
    ...referralList,
    data: newData,
  };
};

const reducer = handleActions<State, AnyAction>({
  [`${combineActions(showReferralAction, createPatientInformationAction, updatePatientInformationAction, createPatientHistoryAction, updatePatientHistoryAction,
    saveReferralInfoAction, saveReferralReasonAction, getReferralMessageThreadAction,
    showLabOrderAction, saveReferralLabInfoAction, saveReferralLabOrderDetailsAction,
    showClinicReferralAction, updateReferralInfoAction, updateReferralLabInfoAction,
  )}`](state: State, { payload }: AnyAction) {
    // Phase 3 (backend): every one of these endpoints now answers {data: ...}
    // instead of the referral directly; unwrap it the same way syncReferralInList
    // already does below, so referralData keeps holding the referral itself.
    const referralData = payload?.data?.id ? payload.data : payload;
    return {
      ...state,
      referralData,
      // BUG-021 fix: keep the cached list entry in sync so an in-place edit
      // (patient info, referral info, etc.) is visible the moment the user
      // closes the modal — without waiting for the next ReferralsPage mount.
      referralList: syncReferralInList(state.referralList, payload),
    };
  },
  [`${updateReferralFilterAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      referralFilter: payload,
    };
  },
  [`${updateReferralReferredByAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      referralReferredBy: payload,
    };
  },
  [`${updateReferralReferredToAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      referralReferredTo: payload,
    };
  },
  [`${updateReferralFollowUpStatusAction}`](state: State, { payload }: AnyAction) {
    // Consolidated to use the same syncReferralInList helper as the other
    // mutation actions (see BUG-021 fix at the top of the file). Behavior
    // is identical for the cases the previous inline pattern handled, with
    // two upgrades: (1) the list keeps the same reference when no entry
    // matches, so subscribers don't re-render unnecessarily, and (2) the
    // helper guards against the empty / undefined list shape that the
    // inline pattern's unguarded `.map()` would silently propagate.
    const updated = payload?.data;
    return {
      ...state,
      referralData:
        state.referralData?.id === updated?.id
          ? { ...state.referralData, ...updated }
          : state.referralData,
      referralList: syncReferralInList(state.referralList, payload),
    };
  },
  [`${updateReferralStatusAction}`](state: State, { payload, params }: AnyAction) {
    const referral: any = { ...state.referralData };
    const userId = params?.userId;
    let newCount: any = { ...state.unreadCount };
    const received = referral?.created_by !== userId;

    if (referral.status === 'pending' && received) {
      newCount = {
        ...newCount,
        received: {
          ...newCount.received,
          pending: state.unreadCount.received.pending - 1,
          all: state.unreadCount.received.all - 1,
        },
        all: {
          ...newCount.all,
          pending: state.unreadCount.all.pending - 1,
          all: state.unreadCount.all.all - 1,
        },
      };

      return {
        ...state,
        referralData: payload?.data?.id ? payload.data : payload,
        // BUG-021 fix: keep cached list entry in sync on status transition.
        referralList: syncReferralInList(state.referralList, payload),
        unreadCount: newCount,
      };
    }

    return {
      ...state,
      referralData: payload?.data?.id ? payload.data : payload,
      // BUG-021 fix: keep cached list entry in sync on status transition.
      referralList: syncReferralInList(state.referralList, payload),
      unreadCount: newCount,
    };
  },
  [`${getReferralMessagesAction}`](state: State, { payload, params }: AnyAction) {
    const paramId: string = params?.id;
    const conversations: any = paramId && state?.conversations ? state?.conversations[paramId] : [];
    const {
      data,
      meta: { current_page: page, last_page: lastPage },
    } = payload;
    const messages = page === 1 ? data : [ ...conversations, ...data ];
    return {
      ...state,
      conversations: {
        ...state.conversations,
        [params.id]: [
          ...messages,
        ],
      },
      pagination: {
        ...state.pagination,
        [params.id]: {
          current_page: page,
          last_page: lastPage,
        },
      },
    };
  },
  [`${displayMessageAction}`](state: State, { payload }: AnyAction) {
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const newReferralData: any = { ...state.referralData };

    if (payload?.userId !== payload?.sender_id && payload?.referral_id === newReferralData?.id) {
      newReferralData.message = payload;

      return {
        ...state,
        referralData: newReferralData,
        conversations: {
          ...state.conversations,
          [payload.referral_id]: [
            payload,
            ...conversations,
          ],
        },
      };
    }

    if (payload?.url && !payload?.userId) {
      if (newReferralData.attachments.length > 0) {
        newReferralData.attachments.unshift(payload?.attachment);
      }

      if (newReferralData.attachments.length === 0) {
        newReferralData.attachments.push(payload?.attachment);
      }

      return {
        ...state,
        referralData: newReferralData,
        conversations: {
          ...state.conversations,
          [payload.referral_id]: [
            payload,
            ...conversations,
          ],
        },
      };
    }

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: [
          payload,
          ...conversations,
        ],
      },
    };
  },
  [`${updateMessageAction}`](state: State, { payload }: AnyAction) {
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const messageIndex = conversations.findIndex((c: any) => c.id === payload.id && !!c.status);

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: [
          ...conversations.slice(0, messageIndex),
          { ...conversations[messageIndex], ...payload },
          ...conversations.slice(messageIndex + 1),
        ],
      },
    };
  },
  [`${deleteMessageAction}`](state: State, { payload }: AnyAction) {
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const updatedConversation = conversations.filter((c: any) => c.id !== payload.id);
    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: updatedConversation,
      },
    };
  },
  [`${sendReferralMessageAction}`](state: State, { payload: rawPayload, params }: AnyAction) {
    // Phase 3 (backend): now answers {data: <message>} instead of the message directly.
    const payload = rawPayload?.data?.referral_id ? rawPayload.data : rawPayload;
    const conversations = state.conversations?.[payload.referral_id] ?? [];
    const messageIndex = conversations.findIndex((c: any) => c.id === params.id && c.status === 'sending');

    if (payload?.url) {
      const newReferralData: any = { ...state.referralData };

      if (newReferralData.attachments.length > 0) {
        newReferralData.attachments.unshift(payload?.attachment);
      }

      if (newReferralData.attachments.length === 0) {
        newReferralData.attachments.push(payload?.attachment);
      }

      return {
        ...state,
        referralData: newReferralData,
        conversations: {
          ...state.conversations,
          [payload.referral_id]: [
            ...conversations.slice(0, messageIndex),
            payload,
            ...conversations.slice(messageIndex + 1),
          ],
        },
      };
    }

    return {
      ...state,
      conversations: {
        ...state.conversations,
        [payload.referral_id]: [
          ...conversations.slice(0, messageIndex),
          payload,
          ...conversations.slice(messageIndex + 1),
        ],
      },
    };
  },
  [`${markReferralConversationReadAction}`](state: State, { params, payload }: AnyAction) {
    const referral: any = { ...state.referralData };
    const userId = params?.userId;
    // const patient: any = referral?.patient;
    const message: any = referral?.message;
    // const sent = referral?.created_by === userId;
    const received = referral?.created_by !== userId;

    // Phase 3 (backend): now answers {data: <read record>} instead of it directly.
    const readRecord = payload?.data ?? payload;

    // if last message already have a read record, return;
    if (message?.reads?.id) return state;

    let newCount: any = state.unreadCount;

    // for received referrals
    if (referral?.status && statusArr.indexOf(referral?.status) !== -1 && received) {
      newCount = {
        ...newCount,
        received: {
          ...newCount.received,
          [referral.status as ReferralStatus]: state.unreadCount
            .received[referral.status as ReferralStatus] - 1,
          all: state.unreadCount.received.all - 1,
        },
        // all of regardless if sent or received
        all: {
          ...newCount.all,
          [referral.status as ReferralStatus]: state.unreadCount
            .all[referral.status as ReferralStatus] - 1,
          all: state.unreadCount.all.all - 1,
        },
      };

      message.reads = readRecord;
      return {
        ...state,
        unreadCount: newCount,
      };
    }

    // for sent referrals
    newCount = {
      ...newCount,
      sent: {
        ...newCount.sent,
        [referral.status as ReferralStatus]: state.unreadCount
          .sent[referral.status as ReferralStatus] - 1,
        all: state.unreadCount.sent.all - 1,
      },
      // all of regardless if sent or received
      all: {
        ...newCount.all,
        [referral.status as ReferralStatus]: state.unreadCount
          .all[referral.status as ReferralStatus] - 1,
        all: state.unreadCount.all.all - 1,
      },
    };

    message.reads = readRecord;

    return {
      ...state,
      unreadCount: newCount,
    };
  },
  [`${markReferralConversationInListReadAction}`](state: State, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <read record>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const { data } = params;
    const type = data?.type ?? 'referral-list';

    // Message-less Drafts use their own explicit per-user state. They must
    // not mutate conversation-based unread counters, because no message or
    // notification was created by this manual triage action.
    if (data?.status === 'draft' && data?.is_message_less_draft) {
      const referrals: any = { ...state.referralList };
      const referralIndex = referrals?.data?.findIndex((r: any) => r.id === data.referral_id) ?? -1;

      if (referralIndex !== -1) {
        referrals.data = [
          ...referrals.data.slice(0, referralIndex),
          {
            ...referrals.data[referralIndex],
            manual_read_state: { is_unread: false },
          },
          ...referrals.data.slice(referralIndex + 1),
        ];
      }

      return { ...state, referralList: referrals };
    }

    let newCount: any = { ...state.unreadCount };
    if (type === 'message-list') {
      const referralMessages: any = { ...state.messageList };
      const messageIndex = referralMessages?.data?.length > 0 && referralMessages.data
        .findIndex((r: any) => r.id === data.referral_id);

      if (messageIndex !== -1) {
        const referral = { ...referralMessages.data[messageIndex] };

        if (referral?.message) {
          if (!referral?.message?.reads?.id) {
            const mode = data?.user_id === referral?.referred_by_id ? 'sent' : 'received';

            newCount = {
              ...newCount,
              [mode]: {
                ...newCount[mode],
                [referral.status as ReferralStatus]: state.unreadCount[mode][referral
                  .status as ReferralStatus] + 1,
                all: state.unreadCount[mode].all - 1,
              },
              all: {
                ...newCount.all,
                [referral.status as ReferralStatus]: state.unreadCount
                  .all[referral.status as ReferralStatus] - 1,
                all: state.unreadCount.all.all - 1,
              },
            };
          }
          referral.message = {
            ...referral.message,
            reads: payload,
          };
        }

        referralMessages.data = [
          ...referralMessages.data.slice(0, messageIndex),
          referral,
          ...referralMessages.data.slice(messageIndex + 1),
        ];
      }

      return {
        ...state,
        messageList: referralMessages,
        unreadCount: newCount,
      };
    }
    const referrals: any = { ...state.referralList };
    const referralIndex = referrals?.data?.length > 0 && referrals.data
      .findIndex((r: any) => r.id === data.referral_id);

    if (referralIndex !== -1) {
      const referral = { ...referrals.data[referralIndex] };

      if (referral?.message) {
        if (!referral?.message?.reads?.id) {
          const mode = data?.user_id === referral?.referred_by_id ? 'sent' : 'received';

          newCount = {
            ...newCount,
            [mode]: {
              ...newCount[mode],
              [referral.status as ReferralStatus]: state.unreadCount[mode][referral
                .status as ReferralStatus] - 1,
              all: state.unreadCount[mode].all - 1,
            },
            all: {
              ...newCount.all,
              [referral.status as ReferralStatus]: state.unreadCount
                .all[referral.status as ReferralStatus] - 1,
              all: state.unreadCount.all.all - 1,
            },
          };
        }
        referral.message = {
          ...referral.message,
          reads: payload,
        };
      }

      referrals.data = [
        ...referrals.data.slice(0, referralIndex),
        referral,
        ...referrals.data.slice(referralIndex + 1),
      ];
    }

    return {
      ...state,
      referralList: referrals,
      unreadCount: newCount,
    };
  },
  [`${markReferralConversationInListUnreadAction}`](state: State, { params }: AnyAction) {
    const { data } = params;
    const type = data?.type ?? 'referral-list';

    // See the matching Mark as Read handler: Draft state is intentionally
    // isolated from message counts and persisted by the API without a chat
    // or system-message side effect.
    if (data?.status === 'draft' && data?.is_message_less_draft) {
      const referrals: any = { ...state.referralList };
      const referralIndex = referrals?.data?.findIndex((r: any) => r.id === data.referral_id) ?? -1;

      if (referralIndex !== -1) {
        referrals.data = [
          ...referrals.data.slice(0, referralIndex),
          {
            ...referrals.data[referralIndex],
            manual_read_state: { is_unread: true },
          },
          ...referrals.data.slice(referralIndex + 1),
        ];
      }

      return { ...state, referralList: referrals };
    }

    let newCount: any = { ...state.unreadCount };
    if (type === 'message-list') {
      const referralMessages: any = { ...state.messageList };
      const messageIndex = referralMessages?.data?.length > 0 && referralMessages.data
        .findIndex((r: any) => r.id === data.referral_id);

      if (messageIndex !== -1) {
        const referral = { ...referralMessages.data[messageIndex] };

        if (referral?.message) {
          if (referral?.message?.reads?.id) {
            const mode = data?.user_id === referral?.referred_by_id ? 'sent' : 'received';

            newCount = {
              ...newCount,
              [mode]: {
                ...newCount[mode],
                [referral.status as ReferralStatus]: state.unreadCount[mode][referral
                  .status as ReferralStatus] + 1,
                all: state.unreadCount[mode].all + 1,
              },
              all: {
                ...newCount.all,
                [referral.status as ReferralStatus]: state.unreadCount
                  .all[referral.status as ReferralStatus] + 1,
                all: state.unreadCount.all.all + 1,
              },
            };
          }

          referral.message = {
            ...referral.message,
            reads: null,
          };
        }

        referralMessages.data = [
          ...referralMessages.data.slice(0, messageIndex),
          referral,
          ...referralMessages.data.slice(messageIndex + 1),
        ];
      }

      return {
        ...state,
        messageList: referralMessages,
        unreadCount: newCount,
      };
    }
    const referrals: any = { ...state.referralList };
    const referralIndex = referrals?.data?.length > 0 && referrals.data
      .findIndex((r: any) => r.id === data.referral_id);

    if (referralIndex !== -1) {
      const referral = { ...referrals.data[referralIndex] };

      if (referral?.message) {
        if (referral?.message?.reads?.id) {
          const mode = data?.user_id === referral?.referred_by_id ? 'sent' : 'received';

          newCount = {
            ...newCount,
            [mode]: {
              ...newCount[mode],
              [referral.status as ReferralStatus]: state.unreadCount[mode][referral
                .status as ReferralStatus] + 1,
              all: state.unreadCount[mode].all + 1,
            },
            all: {
              ...newCount.all,
              [referral.status as ReferralStatus]: state.unreadCount
                .all[referral.status as ReferralStatus] + 1,
              all: state.unreadCount.all.all + 1,
            },
          };
        }
        referral.message = {
          ...referral.message,
          reads: null,
        };
      }

      referrals.data = [
        ...referrals.data.slice(0, referralIndex),
        referral,
        ...referrals.data.slice(referralIndex + 1),
      ];
    }

    return {
      ...state,
      referralList: referrals,
      unreadCount: newCount,
    };
  },
  [`${combineActions(getUnreadNotificationsAction, getUnreadAffiliatedReferralsCountAction, getUnreadPreferredDentistReferralsCountAction)}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): unread-notifications now answers {data: {...}} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    return {
      ...state,
      unreadCount: payload?.referrals,
    };
  },
  [`${updateUnreadReferralMessageCountAction}`](state: State, { payload }: AnyAction) {
    const userId = payload?.userId;
    const payloadData = payload?.data;
    const latestMessages = payloadData?.referred_details?.latest_messages || [];
    const statusHistories = payloadData?.referred_details?.status_histories || [];
    // const patient = payload?.data?.referred_details?.patient;
    const latestMessagesCount = payloadData?.referred_details?.latest_messages?.length || 0;
    const refStatus = payloadData?.referred_details?.status;
    // const sent = payloadData?.referred_details?.created_by === userId;
    const received = payloadData?.referred_details?.created_by !== userId;

    let newCount: any = { ...state.unreadCount };
    let newReferralList: any = { ...state.referralList };
    let newMessageList: any = { ...state.messageList };
    const newReferralData: any = { ...state.referralData };
    if (statusHistories && statusHistories?.length > 0) {
      // A New referral's own system rows ("Referral sent to", edits) arrive with the
      // referral's own event, which counts it; chat on a New referral counts here (ENH-054).
      if (statusHistories[0]?.status === 'pending' && statusHistories[1]?.status === 'draft'
        && payloadData?.message_type === 'system') {
        return {
          ...state,
          unreadCount: newCount,
        };
      }
    }
    // const tab = newReferralList?.tab;

    const { data }: any = newReferralList;
    const { data: messageData }: any = newMessageList;

    const referralIndex = data?.length > 0 && data
      .findIndex((r: any) => r.id === payloadData.referral_id);

    const messageIndex = messageData?.length > 0 && messageData
      .findIndex((r: any) => r.id === payloadData.referral_id);

    // if the user is in another tab/page, just update the count
    // (not for the chat the user has open: they are reading it)
    if (referralIndex === -1 && messageIndex === -1 && !payload?.threadOpen) {
      // if there is only one unread message, update here. A chat reply to the
      // user's own message counts too: `reads` leaves out its sender's record.
      // (Status rows are left as they were: the referral's own event moves them.)
      if (latestMessagesCount === 1
        || (latestMessagesCount === 2 && (latestMessages[1]?.reads?.read_by === userId
          || (payloadData?.message_type !== 'system' && latestMessages[1]?.sender_id === userId)))) {
        // for received referrals
        if (refStatus && statusArr.indexOf(refStatus) !== -1) {
          if (received) {
            newCount = {
              ...newCount,
              received: {
                ...newCount.received,
                [refStatus as ReferralStatus]: state.unreadCount
                  .received[refStatus as ReferralStatus] + 1,
                all: state.unreadCount.received.all + 1,
              },
              // all of regardless if sent or received
              all: {
                ...newCount.all,
                [refStatus as ReferralStatus]: state.unreadCount
                  .all[refStatus as ReferralStatus] + 1,
                all: state.unreadCount.all.all + 1,
              },
            };

            return {
              ...state,
              unreadCount: newCount,
            };
          }

          // for sent referrals
          newCount = {
            ...newCount,
            sent: {
              ...newCount.sent,
              [refStatus as ReferralStatus]: state.unreadCount
                .sent[refStatus as ReferralStatus] + 1,
              all: state.unreadCount.sent.all + 1,
            },
            // all of regardless if sent or received
            all: {
              ...newCount.all,
              [refStatus as ReferralStatus]: state.unreadCount
                .all[refStatus as ReferralStatus] + 1,
              all: state.unreadCount.all.all + 1,
            },
          };

          return {
            ...state,
            unreadCount: newCount,
          };
        }
      }

      return {
        ...state,
        unreadCount: newCount,
      };
    }

    // else update the referral data message record as well
    // will only update the count if the user is outside the message page
    // but currently in the referral/message list page
    if (!newReferralData?.id) {
      // will enter this validation if the current page/tab
      // opened is the message list/if the message list has a value
      if (messageIndex >= 0 && messageData?.length > 0) {
        const [ updatedMessage ] = messageData.splice(messageIndex, 1);

        // Update the message with the new data
        updatedMessage.message = payloadData;

        // Add the updated message to the beginning of the array
        messageData.unshift(updatedMessage);

        newMessageList = {
          ...newMessageList,
          messageData,
        };
      }

      // will enter this validation if the current page/tab
      // opened is the referral list/if the referral list has a value
      if (referralIndex >= 0 && data?.length > 0) {
        const [ updatedData ] = data.splice(referralIndex, 1);

        // Update the message with the new data
        updatedData.message = payloadData;

        // Add the updated data to the beginning of the array
        data.unshift(updatedData);

        newReferralList = {
          ...newReferralList,
          data,
        };
      }

      if (latestMessagesCount === 1
        || (latestMessagesCount === 2 && latestMessages[1]?.reads?.id)) {
        // for received referrals
        if (refStatus && statusArr.indexOf(refStatus) !== -1 && received) {
          newCount = {
            ...newCount,
            received: {
              ...newCount.received,
              [refStatus as ReferralStatus]: state.unreadCount
                .received[refStatus as ReferralStatus] + 1,
              all: state.unreadCount.received.all + 1,
            },
            // all of regardless if sent or received
            all: {
              ...newCount.all,
              [refStatus as ReferralStatus]: state.unreadCount
                .all[refStatus as ReferralStatus] + 1,
              all: state.unreadCount.all.all + 1,
            },
          };

          return {
            ...state,
            unreadCount: newCount,
            referralList: newReferralList,
            messageList: newMessageList,
          };
        }

        // for sent referrals
        newCount = {
          ...newCount,
          sent: {
            ...newCount.sent,
            [refStatus as ReferralStatus]: state.unreadCount
              .sent[refStatus as ReferralStatus] + 1,
            all: state.unreadCount.sent.all + 1,
          },
          // all of regardless if sent or received
          all: {
            ...newCount.all,
            [refStatus as ReferralStatus]: state.unreadCount
              .all[refStatus as ReferralStatus] + 1,
            all: state.unreadCount.all.all + 1,
          },
        };

        return {
          ...state,
          unreadCount: newCount,
          referralList: newReferralList,
          messageList: newMessageList,
        };
      }
    }

    // push attachments to the receiver real time, only into the open chat they belong to
    // (its edit form shares referralData, and must not take chat files as its own)
    if (payload?.threadOpen && payloadData?.attachment
      && payloadData?.referral_id === newReferralData?.id) {
      if (newReferralData.attachments.length > 0) {
        newReferralData.attachments.unshift(payloadData?.attachment);
      }

      if (newReferralData.attachments.length === 0) {
        newReferralData.attachments.push(payloadData?.attachment);
      }
    }

    return {
      ...state,
      unreadCount: newCount,
      referralList: newReferralList,
      messageList: newMessageList,
      referralData: newReferralData,
    };
  },
  // ENH-054: the arithmetic above sees only one other reader of the previous
  // message, so a co-recipient can be counted twice; the server count settles it.
  [`${referralMessageArrivedAction}`](state: State) {
    return {
      ...state,
      liveMessages: (state.liveMessages ?? 0) + 1,
    };
  },
  [`${updateReferralMessageStatusAction}`](state: State, { payload }: AnyAction) {
    const { data: newData } = payload.data;
    const newReferralData: any = { ...state.referralData };

    // if inside the referral's message, update the data
    if (newReferralData?.id === newData?.id) {
      newReferralData.status = newData?.status;
      newReferralData.status_history = newData?.status_history;
      if (newData?.status === 'completed') {
        newReferralData.summary_file_url = newData?.summary_file_url;
        newReferralData.summary_file_size = newData?.summary_file_size;
        newReferralData.summary_filename = newData?.summary_filename;
      }
      return {
        ...state,
        referralData: newReferralData,
      };
    }

    return {
      ...state,
      referralData: state.referralData,
    };
  },
  [`${updateReferralListAction}`](state: State, { payload }: AnyAction) {
    const { data: newData } = payload.data;
    const userId = payload?.userId;
    const newReferralList: any = { ...state.referralList };
    const newMessageList: any = { ...state.messageList };
    const tab = newReferralList?.tab;
    const filter = newReferralList?.filter;
    const sent = newData?.created_by === userId;
    const received = newData?.created_by !== userId;
    const lastMessagesSent = newData?.latest_messages || [];
    const lastMessageCount = newData?.latest_messages?.length || 0;
    const clinicToClinic = (newData?.referred_by_clinic_id === newData?.referred_to_clinic_id);
    let newCount: any = { ...state.unreadCount };

    // if status is not pending, update the data
    if (newData?.status !== 'pending') {
      const { data: refData }: any = newReferralList;
      const { data: messageData }: any = newMessageList;

      const referralIndex = refData && refData?.length > 0
        && refData.findIndex((r: any) => r.id === newData.id);
      const messageIndex = messageData && messageData?.length > 0
        && messageData.findIndex((r: any) => r.id === newData.id);

      // update the count if the status has been changed and has more
      // more than 1 last message count and has never been read
      if (lastMessageCount === 2 && (!lastMessagesSent[1]?.reads?.id
        || lastMessagesSent[1]?.reads?.read_by === userId)) {
        // const lastMessageSent = newData?.last_message_sent;
        const statusHistories = newData?.status_histories;
        // always index 1 since it will only have an array of 2 data
        // const previousMessage = lastMessageSent[1];
        const previousStatus = statusHistories[1];

        if (statusArr.indexOf(previousStatus?.status) !== -1
          && statusArr.indexOf(newData?.status) !== -1) {
          // received referrals
          if (received) {
            newCount = {
              ...newCount,
              received: {
                ...newCount.received,
                // add 1 to the new status
                [newData?.status as ReferralStatus]: state.unreadCount
                  .received[newData?.status as ReferralStatus] + 1,
                // subtract 1 to the previous status
                [previousStatus?.status as ReferralStatus]: state.unreadCount
                  .received[previousStatus?.status as ReferralStatus] - 1,
              },
              // all of regardless if sent or received
              all: {
                ...newCount.all,
                [newData?.status as ReferralStatus]: state.unreadCount
                  .all[newData?.status as ReferralStatus] + 1,
                [previousStatus?.status as ReferralStatus]: state.unreadCount
                  .all[previousStatus?.status as ReferralStatus] - 1,
                // all: state.unreadCount.all.all - 1,
              },
            };
          }
          // sent referrals
          if (sent) {
            newCount = {
              ...newCount,
              sent: {
                ...newCount.sent,
                // add 1 to the new status
                [newData?.status as ReferralStatus]: state.unreadCount
                  .sent[newData?.status as ReferralStatus] + 1,
                // subtract 1 to the previous status
                [previousStatus?.status as ReferralStatus]: state.unreadCount
                  .sent[previousStatus?.status as ReferralStatus] - 1,
              },
              // all of regardless if sent or received
              all: {
                ...newCount.all,
                [newData?.status as ReferralStatus]: state.unreadCount
                  .all[newData?.status as ReferralStatus] + 1,
                [previousStatus?.status as ReferralStatus]: state.unreadCount
                  .all[previousStatus?.status as ReferralStatus] - 1,
                // all: state.unreadCount.all.all - 1,
              },
            };
          }
        }
      }

      // update data list if the user's tab is equal to the data status
      // (ongoing, completed, declined)
      if (tab === newData?.status) {
        // it means the data is not yet visible in the list, add it
        if (referralIndex === -1) {
          if (newReferralList?.data?.length >= newReferralList?.meta?.per_page) {
            // remove the last data
            newReferralList?.data?.splice(9, 1);
            // push the newData above the list
            newReferralList?.data?.unshift(newData);
          }
          if (newReferralList?.data?.length < newReferralList?.meta?.per_page) {
            // push the newData above the list
            newReferralList?.data?.unshift(newData);
          }
          return {
            ...state,
            unreadCount: newCount,
            referralList: newReferralList,
          };
        }
        // it means the data is visible and needs to be removed from the list
        // const filteredData = refData.filter((r: any) => r.id !== newData.id);
        // newReferralList.data = filteredData;
        // return {
        //   ...state,
        //   referralList: newReferralList,
        // };
      }

      // remove data with pending/completed status real time in other tabs
      if ([ 'pending', 'ongoing', 'accepted', 'scheduled' ].indexOf(tab) !== -1) {
        // if the new data is equal to decline, completed or read, place it in the correct tab
        if ([ 'accepted', 'scheduled', 'ongoing', 'completed', 'declined' ].indexOf(newData?.status) !== -1 && referralIndex !== -1) {
          // it means the data is visible and needs to be removed from the list
          const filteredData = refData.filter((r: any) => r.id !== newData.id);
          newReferralList.data = filteredData;

          return {
            ...state,
            unreadCount: newCount,
            referralList: newReferralList,
          };
        }
      }

      // if in the referrals tab, update the list status if the index is existing
      if (referralIndex !== -1 && refData?.[referralIndex]) {
        refData[referralIndex].status = newData?.status;
        return {
          ...state,
          unreadCount: newCount,
          referralList: newReferralList,
        };
      }

      // if in the messages table, update the list status if the index is existing
      if (messageIndex !== -1 && messageData?.[messageIndex]) {
        messageData[messageIndex].status = newData?.status;
        return {
          ...state,
          unreadCount: newCount,
          messageList: newMessageList,
        };
      }

      return {
        ...state,
        unreadCount: newCount,
        messageList: state.messageList,
        referralList: state.referralList,
      };
    }

    // if the status is pending, add the newly created in the list
    // update data list if the user is in the all/pending tab
    if ([ 'all', 'pending' ].indexOf(tab) !== -1 && received && !filter.includes('sent')) {
      if (newReferralList?.data?.length >= newReferralList?.meta?.per_page) {
        // remove the last data
        newReferralList?.data?.splice(9, 1);
        // push the newData above the list
        newReferralList?.data?.unshift(newData);
      }
      if (newReferralList?.data?.length < newReferralList?.meta?.per_page) {
        // push the newData above the list
        newReferralList?.data?.unshift(newData);
      }
    }

    // update received count
    if (received && !clinicToClinic) {
      newCount = {
        ...newCount,
        received: {
          ...newCount.received,
          pending: state.unreadCount.received.pending + 1,
          all: state.unreadCount.received.all + 1,
        },
        // all of regardless if sent or received
        all: {
          ...newCount.all,
          pending: state.unreadCount.all.pending + 1,
          all: state.unreadCount.all.all + 1,
        },
      };
    }
    if (received && clinicToClinic) {
      newCount = {
        ...newCount,
        sent: {
          ...newCount.sent,
          pending: state.unreadCount.sent.pending + 1,
          all: state.unreadCount.sent.all + 1,
        },
        // all of regardless if sent or received
        all: {
          ...newCount.all,
          pending: state.unreadCount.all.pending + 1,
          all: state.unreadCount.all.all + 1,
        },
      };
    }

    // update message list
    if (newMessageList?.data?.length >= newMessageList?.meta?.per_page) {
      // remove the last data
      newMessageList?.data?.splice(9, 1);
      // push the newData above the list
      newMessageList?.data?.unshift(newData);
    }

    if (newMessageList?.data?.length < newMessageList?.meta?.per_page) {
      // push the newData above the list
      newMessageList?.data?.unshift(newData);
    }

    return {
      ...state,
      unreadCount: newCount,
      referralList: newReferralList,
      messageList: newMessageList,
    };
  },
  [`${combineActions(getMessageListAction, getClinicMessageListAction)}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      messageList: payload,
    };
  },
  [`${deleteReferralMessageAttachmentAction}`](state: State, { params }: AnyAction) {
    const newConversations: any = state.conversations?.[params?.data?.referral_id];
    const updatedConversation = newConversations.filter((c: any) => c.id !== params?.data?.id);
    const newReferralData: any = { ...state.referralData };
    const updatedAttachments = newReferralData.attachments
      .filter((a: any) => a.id !== params?.data?.attachment_id);
    newReferralData.attachments = updatedAttachments;
    return {
      ...state,
      referralData: newReferralData,
      conversations: {
        ...state.conversations,
        [params?.data?.referral_id]: updatedConversation,
      },
    };
  },
  [`${deleteReferralAttachmentAction}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <conversation or attachment>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    if (payload?.attachment_id) {
      const newConversations: any = state.conversations?.[payload?.referral_id];
      const updatedConversation = newConversations.filter((c: any) => c.id !== payload?.id);
      const newReferralData: any = { ...state.referralData };
      const updatedAttachments = newReferralData.attachments
        .filter((a: any) => a.id !== payload?.attachment_id);
      newReferralData.attachments = updatedAttachments;

      return {
        ...state,
        referralData: newReferralData,
        conversations: {
          ...state.conversations,
          [payload?.referral_id]: updatedConversation,
        },
      };
    }
    const newReferralData: any = { ...state.referralData };
    const updatedAttachments = newReferralData.attachments
      .filter((a: any) => a.id !== payload?.id);
    newReferralData.attachments = updatedAttachments;
    return {
      ...state,
      referralData: newReferralData,
    };
  },
  [`${deleteReceiverMessageAction}`](state: State, { payload }: AnyAction) {
    const newConversations: any = state.conversations?.[payload?.referral_id];
    const updatedConversation = newConversations.filter((c: any) => c.id !== payload?.id);
    const newReferralData: any = { ...state.referralData };
    const updatedAttachments = newReferralData.attachments
      .filter((a: any) => a.id !== payload?.attachment_id);
    newReferralData.attachments = updatedAttachments;
    return {
      ...state,
      referralData: newReferralData,
      conversations: {
        ...state.conversations,
        [payload?.referral_id]: updatedConversation,
      },
    };
  },
  [`${getReferralMessageAttachmentsAction}`](state: State, { payload, params }: AnyAction) {
    if (params?.for_download_all) {
      return state;
    }
    const newData = payload?.data;
    const newReferralData: any = { ...state.referralData };
    newReferralData.attachments.push(...newData);
    return {
      ...state,
      referralData: newReferralData,
    };
  },
  // update message list as well since the lab orders are included for Clinic and Dentist POV
  [`${updateUnreadLabOrderMessageCountAction}`](state: State, { payload }: AnyAction) {
    const payloadData = payload?.data;

    let newMessageList: any = state.messageList;
    // const tab = newReferralList?.tab;

    const messageData: any = newMessageList?.data ?? [];

    const messageIndex = messageData?.length > 0 && messageData
      .findIndex((r: any) => r.id === payloadData.referral_id);

    // will enter this validation if the current page/tab
    // opened is the message list/if the message list has a value
    if (messageIndex >= 0 && messageData?.length > 0) {
      const [ updatedMessage ] = messageData.splice(messageIndex, 1);

      // Update the message with the new data
      updatedMessage.message = payloadData;

      // Add the updated message to the beginning of the array
      messageData.unshift(updatedMessage);

      newMessageList = {
        ...newMessageList,
        messageData,
      };
    }

    return {
      ...state,
      messageList: newMessageList,
    };
  },
  [`${markLabOrderConversationInListReadAction}`](state: State, { params, payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: <read record>} instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const { data } = params;
    const type = data?.type ?? 'lab-order-list';
    if (type === 'message-list') {
      const messages: any = { ...state.messageList };
      const messageIndex = messages?.data?.length > 0 && messages.data
        .findIndex((r: any) => r.id === data.lab_order_id);

      if (messageIndex !== -1) {
        const labOrder = { ...messages.data[messageIndex] };

        if (labOrder?.message) {
          if (!labOrder?.message?.reads?.id) {
            labOrder.message = {
              ...labOrder.message,
              reads: payload,
            };
          }
        }

        messages.data = [
          ...messages.data.slice(0, messageIndex),
          labOrder,
          ...messages.data.slice(messageIndex + 1),
        ];
      }

      return {
        ...state,
        messageList: messages,
      };
    }

    return {
      ...state,
    };
  },
  [`${markLabOrderConversationInListUnreadAction}`](state: State, { params }: AnyAction) {
    const { data } = params;
    const type = data?.type ?? 'lab-order-list';
    if (type === 'message-list') {
      const messages: any = { ...state.messageList };
      const messageIndex = messages?.data?.length > 0 && messages.data
        .findIndex((r: any) => r.id === data.lab_order_id);

      if (messageIndex !== -1) {
        const labOrder = { ...messages.data[messageIndex] };

        if (labOrder?.message) {
          if (labOrder?.message?.reads?.id) {
            labOrder.message = {
              ...labOrder.message,
              reads: null,
            };
          }
        }

        messages.data = [
          ...messages.data.slice(0, messageIndex),
          labOrder,
          ...messages.data.slice(messageIndex + 1),
        ];
      }

      return {
        ...state,
        messageList: messages,
      };
    }

    return {
      ...state,
    };
  },
  [`${markAsUrgentAction}`](state: State, { payload }: AnyAction) {
    return {
      ...state,
      isUrgent: payload,
    };
  },
  [`${clearReferralDataAction}`](state: State) {
    return {
      ...state,
      referralData: {},
    };
  },
  [`${logoutAction}`](state) {
    return {
      ...state,
      unreadCount: defaultState.unreadCount,
    };
  },
  [`${combineActions(
    getReferralsAction,
    getClinicReferralsAction,
    getAffiliatedReferralsAction,
    getPreferredDentistReferralAction,
    getAffiliatedDentistReferralsAction,
  )}`](state, { params, payload }: AnyAction) {
    const filter = (params?.filter as FilterType) || 'all';
    const status = (params?.status as ReferralStatus) || 'all';
    const newReferralList = {
      ...payload,
      tab: status,
      filter,
    };
    return {
      ...state,
      referralList: newReferralList,
      filter,
    };
  },
  [`${generateSummaryFileRequestAction}`](state: State, { payload: rawPayload }: AnyAction) {
    // Phase 3 (backend): now answers {data: {file_url, file_size, file_name}}
    // instead of it directly.
    const payload = rawPayload?.data ?? rawPayload;
    const newReferralData: any = { ...state.referralData };
    newReferralData.summary_file_size = payload?.file_size ?? newReferralData?.summary_file_size;
    newReferralData.summary_filename = payload?.file_name ?? newReferralData?.summary_filename;
    return {
      ...state,
      referralData: newReferralData,
    };
  },

}, defaultState);

export default reducer;
